import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import { useOptic } from "@/hooks/useOptic";
import { useDevice } from "@/hooks/useDevice";
import { useSetBottleLocation } from "@/hooks/useCryodeckBottles";
import { Loading } from "@/components/common/Loading";
import { Pathogen, formatCopies } from "@/components/common/Pathogen";
import { Tile, TileGrid, PreviewBanner } from "@/components/common/Tile";
import { SampleSet } from "@/api/qpcr";

const WELL_COLORS = [
  "#2563EB", "#DC2626", "#7C3AED", "#059669",
  "#CA8A04", "#0EA5E9", "#DB2777", "#65A30D",
];

interface ChartPoint {
  cycle: number;
  [series: string]: number | null;
}

function pathogenLabel(s: SampleSet, idx: number): string {
  return s.pathogen_name ?? `pathogen ${idx + 1}`;
}

function CombinedCurvePlot({ sampleSets }: { sampleSets: SampleSet[] }) {
  const { data, series } = useMemo(() => {
    const maxLen = Math.max(
      0,
      ...sampleSets.flatMap((s) => [
        s.control_positive.length,
        s.control_ntc.length,
        ...s.wells.map((w) => w.length),
      ]),
    );
    const seriesNames: string[] = [];
    sampleSets.forEach((s, sIdx) => {
      const label = pathogenLabel(s, sIdx);
      s.wells.forEach((_, wIdx) => seriesNames.push(`${label} · replicate ${wIdx + 1}`));
    });
    const rows: ChartPoint[] = [];
    for (let c = 0; c < maxLen; c++) {
      const point: ChartPoint = { cycle: c + 1 };
      sampleSets.forEach((s, sIdx) => {
        const label = pathogenLabel(s, sIdx);
        s.wells.forEach((well, wIdx) => {
          point[`${label} · replicate ${wIdx + 1}`] = well[c] ?? null;
        });
      });
      rows.push(point);
    }
    return { data: rows, series: seriesNames };
  }, [sampleSets]);

  if (data.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-textLight text-sm">
        No curve data to plot.
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 56, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="cycle"
            stroke="#6B7280"
            fontSize={11}
            label={{ value: "Cycle Number", position: "insideBottom", offset: -8, fontSize: 11 }}
          />
          <YAxis
            stroke="#6B7280"
            fontSize={11}
            label={{ value: "RFU", angle: -90, position: "insideLeft", fontSize: 11 }}
          />
          <Tooltip contentStyle={{ fontSize: 12 }} />
          <Legend verticalAlign="bottom" align="center" wrapperStyle={{ fontSize: 10, paddingTop: 18 }} />
          {series.map((name, idx) => (
            <Line
              key={name}
              type="monotone"
              dataKey={name}
              stroke={WELL_COLORS[idx % WELL_COLORS.length]}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
              strokeWidth={1.5}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function Badge({
  tone,
  children,
}: {
  tone: "info" | "success" | "danger" | "warning" | "neutral";
  children: React.ReactNode;
}) {
  const toneClasses = {
    info: "bg-info-bg text-info-text",
    success: "bg-success-bg text-success-text",
    danger: "bg-danger-bg text-danger-text",
    warning: "bg-warning-bg text-warning-text",
    neutral: "bg-bg text-textLight",
  }[tone];
  return (
    <div className={`rounded-md px-sm py-xs text-xs ${toneClasses}`}>{children}</div>
  );
}

function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-lg bg-surface shadow-card p-lg space-y-md">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-textLight hover:text-text text-sm"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ActionItem({
  onClick,
  disabled,
  title,
  children,
}: {
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="w-full text-left text-sm px-sm py-sm rounded-md hover:bg-bg disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}


interface CurveStats {
  peakRfu: number | null;
  peakCycle: number | null;
  ctEstimate: number | null;
  baselineMean: number | null;
}

function analyzeCurve(values: (number | null)[]): CurveStats {
  const clean = values
    .map((v, i) => ({ v, i }))
    .filter((p): p is { v: number; i: number } => typeof p.v === "number");
  if (clean.length === 0) {
    return { peakRfu: null, peakCycle: null, ctEstimate: null, baselineMean: null };
  }
  let peakIdx = 0;
  for (let i = 1; i < clean.length; i++) {
    if (clean[i].v > clean[peakIdx].v) peakIdx = i;
  }
  const peakRfu = clean[peakIdx].v;
  const peakCycle = clean[peakIdx].i + 1;
  // CT estimate: first cycle where signal crosses half of (peak - baseline mean).
  const baselineSlice = clean.slice(0, Math.min(5, clean.length));
  const baselineMean =
    baselineSlice.reduce((s, x) => s + x.v, 0) / baselineSlice.length;
  const threshold = baselineMean + (peakRfu - baselineMean) * 0.5;
  const crossing = clean.find(p => p.v >= threshold);
  const ctEstimate = crossing ? crossing.i + 1 : null;
  return { peakRfu, peakCycle, ctEstimate, baselineMean };
}

function ReplicateStatsTable({ sampleSet }: { sampleSet: SampleSet }) {
  const rows = sampleSet.wells.map((w, i) => ({ idx: i + 1, ...analyzeCurve(w) }));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-textLight border-b border-border">
            <th className="py-xs pr-md font-medium">Replicate</th>
            <th className="py-xs pr-md font-medium">Peak RFU</th>
            <th className="py-xs pr-md font-medium">Peak cycle</th>
            <th className="py-xs pr-md font-medium">CT est.</th>
            <th className="py-xs font-medium">Baseline μ</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map(r => (
            <tr key={r.idx}>
              <td className="py-xs pr-md text-text">#{r.idx}</td>
              <td className="py-xs pr-md text-text tabular-nums">{r.peakRfu?.toFixed(0) ?? "—"}</td>
              <td className="py-xs pr-md text-text tabular-nums">{r.peakCycle ?? "—"}</td>
              <td className="py-xs pr-md text-text tabular-nums">{r.ctEstimate ?? "—"}</td>
              <td className="py-xs text-text tabular-nums">{r.baselineMean?.toFixed(1) ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SingleResult() {
  const { deviceId = "", opticId = "" } = useParams();
  const navigate = useNavigate();

  const opticQ = useOptic(opticId);
  const deviceQ = useDevice(deviceId);
  const setBottleLocation = useSetBottleLocation();

  const [showBottle, setShowBottle] = useState(false);
  const [bottleLabel, setBottleLabel] = useState("");
  const [showUserData, setShowUserData] = useState(false);
  const [userDataFields, setUserDataFields] = useState<Record<string, string>>({});

  if (opticQ.isLoading || !opticQ.data) return <Loading label="Loading result…" />;
  if (opticQ.error) return <div className="text-danger-text text-sm">Failed to load result.</div>;

  const optic = opticQ.data;
  const device = deviceQ.data;
  const sampleSets = optic.sample_sets ?? [];
  const ranDuplicates = sampleSets.length === 1;
  const cryodeckBottleId = optic.cryodeck_bottle_issued_id;

  const userDataConfig = (device?.config as any)?.userdata as
    | Array<{ index: string | number; name: string; datatype?: string }>
    | undefined;

  return (
    <div className="space-y-md">
      <div className="flex items-center justify-between">
        <Link
          to={`/devices/${deviceId}/results`}
          className="text-sm text-textLight hover:underline"
        >
          ← Back to results
        </Link>
        <div className="text-xs text-textLight font-mono">{optic.id}</div>
      </div>

      <PreviewBanner>
        Quantification &amp; control-pass flags require a backend update — curve shapes are real,
        analysis-derived values are placeholders.
      </PreviewBanner>

      <TileGrid>
        {/* Big combined curve plot */}
        <Tile
          title="qPCR Relative Fluorescence (RFU vs Cycle Number)"
          className="col-span-12 lg:col-span-6 h-[400px]"
        >
          <CombinedCurvePlot sampleSets={sampleSets} />
        </Tile>

        {/* Per-pathogen alert stacks */}
        {sampleSets.map((s, idx) => {
          const label = pathogenLabel(s, idx);
          return (
            <Tile
              key={s.id ?? idx}
              title={<Pathogen name={label} />}
              className="col-span-12 sm:col-span-6 lg:col-span-2 h-[400px]"
            >
              <div className="flex-1 flex flex-col gap-sm overflow-y-auto py-xs">
                {cryodeckBottleId != null && (
                  <Badge tone="info">Cryodeck Bottle #{cryodeckBottleId}</Badge>
                )}
                {ranDuplicates && <Badge tone="info">Target Duplicated</Badge>}
                <Badge tone="neutral">Status pending</Badge>
                <Badge tone="neutral">Standard curve pending</Badge>
              </div>
              <div className="mt-sm">
                <Badge tone="neutral">
                  Quantification:&nbsp;<b>{formatCopies(null)} copies/mL</b>
                </Badge>
              </div>
            </Tile>
          );
        })}

        {/* Curve analysis — derived per-replicate stats for the whole result */}
        {sampleSets.length > 0 && (
          <Tile title="Curve Analysis" className="col-span-12 lg:col-span-10">
            <div className="flex flex-col gap-md">
              {sampleSets.map((ss, idx) => (
                <div key={ss.id ?? idx} className="flex flex-col gap-xs">
                  <div className="text-xs font-medium text-text">
                    <Pathogen name={pathogenLabel(ss, idx)} />
                  </div>
                  <ReplicateStatsTable sampleSet={ss} />
                </div>
              ))}
            </div>
          </Tile>
        )}

        {/* Action list */}
        <Tile title="Actions" className="col-span-12 lg:col-span-2">
          <div className="flex flex-col gap-xs">
            <ActionItem onClick={() => navigate("./details")}>Audit Quantification</ActionItem>
            <ActionItem disabled title="Premium feature">Print Results</ActionItem>
            <ActionItem disabled title="Premium feature">Export Result</ActionItem>
            <ActionItem disabled title="Premium feature">Share Result</ActionItem>
            {userDataConfig && userDataConfig.length > 0 && (
              <ActionItem onClick={() => setShowUserData(true)}>Add User Data</ActionItem>
            )}
            {cryodeckBottleId != null && (
              <ActionItem onClick={() => setShowBottle(true)}>Resolve Cryodeck Bottle</ActionItem>
            )}
          </div>
        </Tile>
      </TileGrid>

      {/* Cryodeck bottle modal */}
      <Modal
        open={showBottle}
        onClose={() => setShowBottle(false)}
        title="Resolve Cryodeck Bottle"
      >
        <div className="space-y-sm">
          <label className="block text-sm">
            <span className="text-textLight">Bottle #{cryodeckBottleId} location label</span>
            <input
              type="text"
              value={bottleLabel}
              onChange={(e) => setBottleLabel(e.target.value)}
              placeholder="e.g. Freezer A · Shelf 2"
              className="mt-xs w-full rounded-md border border-border px-sm py-xs text-sm bg-surface"
            />
          </label>
          {setBottleLocation.isError && (
            <div className="text-xs text-danger-text">Save failed. Try again.</div>
          )}
          <div className="flex justify-end gap-sm pt-sm">
            <button
              onClick={() => setShowBottle(false)}
              className="rounded-md border border-border px-md py-xs text-sm"
            >
              Cancel
            </button>
            <button
              disabled={!bottleLabel || setBottleLocation.isPending || cryodeckBottleId == null}
              onClick={async () => {
                if (cryodeckBottleId == null) return;
                await setBottleLocation.mutateAsync({
                  bottleId: cryodeckBottleId,
                  location: bottleLabel,
                });
                setShowBottle(false);
                setBottleLabel("");
              }}
              className="rounded-md bg-info-text text-white px-md py-xs text-sm disabled:opacity-50"
            >
              {setBottleLocation.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </Modal>

      {/* User data modal */}
      <Modal
        open={showUserData}
        onClose={() => setShowUserData(false)}
        title="Add User-Defined Data"
      >
        <div className="space-y-sm">
          <PreviewBanner>
            Save endpoint not yet wired — values entered here are not persisted.
          </PreviewBanner>
          {userDataConfig?.map((item) => (
            <label key={String(item.index)} className="block text-sm">
              <span className="text-textLight">{item.name}</span>
              <input
                type={item.datatype === "number" ? "number" : "text"}
                value={userDataFields[String(item.index)] ?? ""}
                onChange={(e) =>
                  setUserDataFields((prev) => ({
                    ...prev,
                    [String(item.index)]: e.target.value,
                  }))
                }
                className="mt-xs w-full rounded-md border border-border px-sm py-xs text-sm bg-surface"
              />
            </label>
          ))}
          <div className="flex justify-end gap-sm pt-sm">
            <button
              onClick={() => setShowUserData(false)}
              className="rounded-md border border-border px-md py-xs text-sm"
            >
              Cancel
            </button>
            <button
              onClick={() => setShowUserData(false)}
              className="rounded-md bg-info-text text-white px-md py-xs text-sm"
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
